import { Box, Button, Textarea, Typography } from "@mui/joy";
import CircularProgress from "@mui/material/CircularProgress";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isAdminRole, useRole } from "../Contexts/RoleContext";
import { Util } from "../Helpers/Util";

interface Comment {
	service_report_comments_id: string;
	comment_text: string;
	date_entered: string;
	created_by: string;
}

interface Picture {
	service_report_pictures_id: string;
	url: string;
}

interface ElevatorLine {
	service_report_elevators_id: string;
	report_type: string;
	line_comments?: string;
	serial_number?: string;
	elevator_type?: string;
	elevator_description?: string;
	pictures: Picture[];
	comments: Comment[];
}

interface BuildingLine {
	service_report_buildings_id: string;
	buildings_id: string;
	building_name?: string;
	elevators: ElevatorLine[];
	comments: Comment[];
}

interface ServiceReportDetail {
	service_reports_id: string;
	report_date: string;
	entered_by: string;
	device_saved_at: string;
	server_received_at: string;
	comments: Comment[];
	buildings: BuildingLine[];
}

function CommentList(props: { comments: Comment[]; onAdd: (text: string) => Promise<void> }) {
	const [text, setText] = React.useState("");
	const [saving, setSaving] = React.useState(false);
	const [error, setError] = React.useState("");

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 1 }}>
			<Typography level="title-sm">Comments</Typography>
			{(props.comments ?? []).map((comment) => (
				<Box key={comment.service_report_comments_id}>
					<Typography level="body-xs">
						{comment.created_by} · {Util.Formatter(comment.date_entered, "DT")}
					</Typography>
					<Typography level="body-sm" sx={{ whiteSpace: "pre-wrap" }}>
						{comment.comment_text}
					</Typography>
				</Box>
			))}
			<Textarea minRows={2} value={text} placeholder="Add a comment" onChange={(event) => setText(event.target.value)} />
			{error && (
				<Typography level="body-sm" color="danger">
					{error}
				</Typography>
			)}
			<Button
				size="sm"
				sx={{ alignSelf: "flex-start" }}
				disabled={saving || text.trim() === ""}
				onClick={() => {
					setSaving(true);
					setError("");
					props
						.onAdd(text.trim())
						.then(() => setText(""))
						.catch((ex) => setError(ex.message || "Could not add comment."))
						.finally(() => setSaving(false));
				}}>
				Add Comment
			</Button>
		</Box>
	);
}

export default function ServiceReportDetailPage() {
	const { id } = useParams();
	const navigate = useNavigate();
	const { effectiveRole } = useRole();
	const [report, setReport] = React.useState<ServiceReportDetail | null>(null);
	const [error, setError] = React.useState("");
	const [loading, setLoading] = React.useState(true);
	const staff = isAdminRole(effectiveRole);

	const load = React.useCallback(() => {
		if (!staff || !id) return Promise.resolve();
		setLoading(true);
		return fetch("ServiceReport/GetServiceReport", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ serviceReportsId: id }),
		})
			.then((response) => response.json())
			.then((body) => {
				if (body && body.error) {
					setError(body.error);
					setReport(null);
				} else {
					setError("");
					setReport(body);
				}
				setLoading(false);
			})
			.catch(() => {
				setError("Could not load this service report.");
				setLoading(false);
			});
	}, [id, staff]);

	React.useEffect(() => {
		load();
	}, [load]);

	const addComment = (text: string, target: { serviceReportsId?: string; serviceReportBuildingsId?: string; serviceReportElevatorsId?: string }) => {
		return fetch("ServiceReport/AddComment", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ commentText: text, ...target }),
		})
			.then((response) => response.json())
			.then((body) => {
				if (body && body.error) throw new Error(body.error);
				return load();
			});
	};

	if (!staff) return <Typography>Service reports are not available for this login.</Typography>;
	if (loading) return <CircularProgress size={28} />;
	if (error || !report) return <Typography>{error || "Service report was not found."}</Typography>;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 3 }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center" }}>
				<Typography level="h4" sx={{ m: 0 }}>
					Service Report
				</Typography>
				<Button variant="outlined" onClick={() => navigate("/servicereports")}>
					Back
				</Button>
			</Box>
			<Box>
				<Typography>Date: {Util.Formatter(report.report_date, "D")}</Typography>
				<Typography>Entered by: {report.entered_by}</Typography>
				<Typography>Saved on phone: {Util.Formatter(report.device_saved_at, "DT")}</Typography>
				<Typography>Received: {Util.Formatter(report.server_received_at, "DT")}</Typography>
			</Box>
			<CommentList comments={report.comments ?? []} onAdd={(text) => addComment(text, { serviceReportsId: report.service_reports_id })} />
			{(report.buildings ?? []).map((building) => (
				<Box key={building.service_report_buildings_id} sx={{ border: "1px solid", borderColor: "divider", p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
					<Typography level="title-lg">{building.building_name || "Building"}</Typography>
					<CommentList comments={building.comments ?? []} onAdd={(text) => addComment(text, { serviceReportBuildingsId: building.service_report_buildings_id })} />
					{(building.elevators ?? []).map((elevator) => {
						const serial = elevator.serial_number ? `Serial #${elevator.serial_number}` : "Elevator";
						const detail = [elevator.elevator_description, elevator.elevator_type].filter(Boolean).join(" · ");
						return (
							<Box key={elevator.service_report_elevators_id} sx={{ pl: 1.5, borderLeft: "3px solid", borderColor: "divider", display: "flex", flexDirection: "column", gap: 1 }}>
								<Typography level="title-md">
									{serial}
									{detail ? ` · ${detail}` : ""} · {elevator.report_type}
								</Typography>
								{elevator.line_comments ? (
									<Typography level="body-sm" sx={{ whiteSpace: "pre-wrap" }}>
										Field notes: {elevator.line_comments}
									</Typography>
								) : null}
								{(elevator.pictures ?? []).length > 0 && (
									<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
										{elevator.pictures.map((picture) => (
											<a key={picture.service_report_pictures_id} href={picture.url} target="_blank" rel="noreferrer">
												<img src={picture.url} alt="" style={{ width: 140, height: 140, objectFit: "cover" }} />
											</a>
										))}
									</Box>
								)}
								<CommentList comments={elevator.comments ?? []} onAdd={(text) => addComment(text, { serviceReportElevatorsId: elevator.service_report_elevators_id })} />
							</Box>
						);
					})}
				</Box>
			))}
		</Box>
	);
}
